import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  Zap, Home, Droplets, RefreshCw, CreditCard, Banknote, 
  ArrowLeft, FileText, IndianRupee, Building2, Trash2 
} from 'lucide-react';

const UtilityLedger = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ledger');
  
  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Type-wise ledger state (like vendor ledger)
  const [utilityTypes, setUtilityTypes] = useState([]);
  const [selectedUtilityType, setSelectedUtilityType] = useState(null);
  const [typeLedgerDetails, setTypeLedgerDetails] = useState({ bills: [], payments: [], summary: {} });
  const [loadingTypeLedger, setLoadingTypeLedger] = useState(false);
  const [ledgerFilter, setLedgerFilter] = useState('total_business');
  const [ledgerCustomStart, setLedgerCustomStart] = useState('');
  const [ledgerCustomEnd, setLedgerCustomEnd] = useState('');
  
  // Pending bills state (for payment)
  const [thisMonthBills, setThisMonthBills] = useState([]);
  const [oldMonthBills, setOldMonthBills] = useState([]);
  const [pendingTab, setPendingTab] = useState('this_month');
  const [selectedBills, setSelectedBills] = useState({});
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [paying, setPaying] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  
  const [summary, setSummary] = useState({
    thisMonthTotal: 0,
    thisMonthPending: 0,
    oldMonthTotal: 0,
    oldMonthPending: 0
  });

  const UTILITY_ICONS = {
    'rent_manpreet': Home,
    'rent_gavvy': Home,
    'electricity': Zap,
    'water': Droplets,
    'gst': FileText,
    'vat': FileText,
    'monthly_excise': FileText,
    'monthly_pos': CreditCard
  };

  // Get date range based on filter
  const getDateRange = useCallback(() => {
    const today = new Date();
    let start, end;
    
    switch (ledgerFilter) {
      case 'today':
        start = end = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        start = end = yesterday.toISOString().split('T')[0];
        break;
      case 'last_7_days':
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 7);
        start = last7.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'this_month':
        start = today.toISOString().slice(0, 7) + '-01';
        end = today.toISOString().split('T')[0];
        break;
      case 'total_business':
        start = '2020-01-01';
        end = today.toISOString().split('T')[0];
        break;
      case 'custom':
        start = ledgerCustomStart || '2020-01-01';
        end = ledgerCustomEnd || today.toISOString().split('T')[0];
        break;
      default:
        start = '2020-01-01';
        end = today.toISOString().split('T')[0];
    }
    
    return { start, end };
  }, [ledgerFilter, ledgerCustomStart, ledgerCustomEnd]);

  // Fetch type-wise summary
  const fetchTypeSummary = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { start, end } = getDateRange();
      
      const response = await axios.get(`${API}/utility-bills/type-wise-summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: start, end_date: end }
      });
      
      setUtilityTypes(response.data.utility_types || []);
    } catch (error) {
      console.error('Failed to fetch utility type summary:', error);
      toast.error('Failed to load utility ledger');
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  // Fetch detailed ledger for selected type
  const fetchTypeLedgerDetails = useCallback(async (billType) => {
    setLoadingTypeLedger(true);
    try {
      const token = localStorage.getItem('token');
      const { start, end } = getDateRange();
      
      const response = await axios.get(`${API}/utility-bills/type-ledger/${billType}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: start, end_date: end }
      });
      
      setTypeLedgerDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch type ledger details:', error);
      toast.error('Failed to load ledger details');
    } finally {
      setLoadingTypeLedger(false);
    }
  }, [getDateRange]);

  // Fetch pending utility bills
  const fetchPendingBills = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API}/utility-bills/all-pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const bills = response.data || [];
      const currentMonth = new Date().toISOString().slice(0, 7);
      
      const thisMonth = bills.filter(b => b.bill_month === currentMonth);
      const oldMonths = bills.filter(b => b.bill_month < currentMonth);
      
      setThisMonthBills(thisMonth);
      setOldMonthBills(oldMonths);
      
      setSummary({
        thisMonthTotal: thisMonth.reduce((sum, b) => sum + (b.amount || 0), 0),
        thisMonthPending: thisMonth.reduce((sum, b) => sum + (b.balance || 0), 0),
        oldMonthTotal: oldMonths.reduce((sum, b) => sum + (b.amount || 0), 0),
        oldMonthPending: oldMonths.reduce((sum, b) => sum + (b.balance || 0), 0)
      });
      
    } catch (error) {
      console.error('Failed to fetch pending bills:', error);
    }
  };

  useEffect(() => {
    fetchTypeSummary();
    fetchPendingBills();
  }, []);

  // Refetch when filter changes
  useEffect(() => {
    if (activeTab === 'ledger') {
      fetchTypeSummary();
      if (selectedUtilityType) {
        fetchTypeLedgerDetails(selectedUtilityType.bill_type);
      }
    }
  }, [ledgerFilter, ledgerCustomStart, ledgerCustomEnd, activeTab, fetchTypeSummary, selectedUtilityType, fetchTypeLedgerDetails]);

  const handleSelectUtilityType = (type) => {
    setSelectedUtilityType(type);
    fetchTypeLedgerDetails(type.bill_type);
  };

  const handleBackToAllTypes = () => {
    setSelectedUtilityType(null);
    setTypeLedgerDetails({ bills: [], payments: [], summary: {} });
  };

  // Payment functions
  const toggleBillSelection = (billId, balance) => {
    setSelectedBills(prev => {
      const newSelected = { ...prev };
      if (newSelected[billId]) {
        delete newSelected[billId];
        setPaymentAmounts(pa => {
          const newAmounts = { ...pa };
          delete newAmounts[billId];
          return newAmounts;
        });
      } else {
        newSelected[billId] = true;
        setPaymentAmounts(pa => ({ ...pa, [billId]: balance }));
      }
      return newSelected;
    });
  };

  const updatePaymentAmount = (billId, amount, maxBalance) => {
    const numAmount = parseFloat(amount) || 0;
    setPaymentAmounts(prev => ({
      ...prev,
      [billId]: Math.min(numAmount, maxBalance)
    }));
  };

  const getTotalPayment = () => {
    return Object.entries(paymentAmounts)
      .filter(([id]) => selectedBills[id])
      .reduce((sum, [, amount]) => sum + (parseFloat(amount) || 0), 0);
  };

  const getSelectedCount = () => {
    return Object.keys(selectedBills).length;
  };

  const handleOpenPaymentDialog = () => {
    if (getSelectedCount() === 0) {
      toast.error('Please select at least one bill to pay');
      return;
    }
    setPaymentDialog(true);
  };

  const handlePayBills = async () => {
    if (paying) return;
    
    setPaying(true);
    try {
      const token = localStorage.getItem('token');
      const payments = Object.entries(paymentAmounts)
        .filter(([id]) => selectedBills[id])
        .map(([bill_id, amount]) => ({ bill_id, amount }))
        .filter(p => p.amount > 0);

      if (paymentMethod === 'bank') {
        await axios.post(`${API}/utility-bills/pay-bank-batch`, {
          payments: payments
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        toast.info('For cash payments, please use "Pay Utility Bills" button in Day Closing form');
        setPaymentDialog(false);
        setPaying(false);
        return;
      }

      toast.success(`Paid ${payments.length} utility bill(s) via bank`);
      setPaymentDialog(false);
      setSelectedBills({});
      setPaymentAmounts({});
      fetchPendingBills();
      fetchTypeSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pay utility bills');
    } finally {
      setPaying(false);
    }
  };

  // Delete utility bill (Admin only)
  const handleDeleteUtilityBill = async (billId, billType, hasPayments) => {
    const warningMessage = hasPayments
      ? 'This bill has partial payments. Please contact support to handle payment reversals first.'
      : `Are you sure you want to delete this ${billType} utility bill? This action cannot be undone.`;
    
    if (hasPayments) {
      toast.error(warningMessage);
      return;
    }
    
    if (!window.confirm(warningMessage)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/utility-bills/${billId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Utility bill deleted successfully');
      fetchPendingBills();
      fetchTypeSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete utility bill');
    }
  };

  const selectAllInPendingTab = () => {
    const bills = pendingTab === 'this_month' ? thisMonthBills : oldMonthBills;
    const newSelected = {};
    const newAmounts = {};
    bills.forEach(bill => {
      if (bill.balance > 0) {
        newSelected[bill.id] = true;
        newAmounts[bill.id] = bill.balance;
      }
    });
    setSelectedBills(newSelected);
    setPaymentAmounts(newAmounts);
  };

  const clearSelection = () => {
    setSelectedBills({});
    setPaymentAmounts({});
  };

  const getIcon = (billType) => {
    const Icon = UTILITY_ICONS[billType] || Zap;
    return <Icon className="w-4 h-4" />;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-800">Paid</Badge>;
      case 'partially_paid':
        return <Badge className="bg-yellow-100 text-yellow-800">Partial</Badge>;
      default:
        return <Badge className="bg-red-100 text-red-800">Pending</Badge>;
    }
  };

  // Render pending bills table (for payment tab)
  const renderPendingBillsTable = (bills) => {
    if (bills.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No pending utility bills found
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead className="w-10"></TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Month</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-right">Paid</TableHead>
            <TableHead className="text-right">Balance</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Pay Amount</TableHead>
            {user.role === 'admin' && <TableHead className="text-center">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {bills.map((bill) => {
            const hasPaidAmount = (bill.amount || 0) !== (bill.balance || 0);
            return (
            <TableRow 
              key={bill.id} 
              className={selectedBills[bill.id] ? 'bg-cyan-50' : ''}
            >
              <TableCell>
                <Checkbox 
                  checked={!!selectedBills[bill.id]}
                  onCheckedChange={() => toggleBillSelection(bill.id, bill.balance)}
                  disabled={bill.balance <= 0}
                />
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {getIcon(bill.bill_type)}
                  <span className="font-medium">{bill.bill_type_label || bill.bill_type}</span>
                </div>
              </TableCell>
              <TableCell>{bill.bill_month}</TableCell>
              <TableCell className="text-right">₹{bill.amount?.toLocaleString()}</TableCell>
              <TableCell className="text-right text-green-600">
                ₹{((bill.amount || 0) - (bill.balance || 0)).toLocaleString()}
              </TableCell>
              <TableCell className="text-right font-semibold text-red-600">
                ₹{bill.balance?.toLocaleString()}
              </TableCell>
              <TableCell>{getStatusBadge(bill.status)}</TableCell>
              <TableCell className="text-right">
                {selectedBills[bill.id] ? (
                  <Input
                    type="number"
                    step="100"
                    className="w-28 text-right"
                    value={paymentAmounts[bill.id] || ''}
                    onChange={(e) => updatePaymentAmount(bill.id, e.target.value, bill.balance)}
                  />
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </TableCell>
              {user.role === 'admin' && (
                <TableCell className="text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={hasPaidAmount ? "text-gray-400 cursor-not-allowed" : "text-red-600 hover:text-red-800 hover:bg-red-50"}
                    onClick={() => handleDeleteUtilityBill(bill.id, bill.bill_type_label || bill.bill_type, hasPaidAmount)}
                    title={hasPaidAmount ? "Cannot delete bill with payments" : "Delete utility bill"}
                    data-testid={`delete-utility-bill-${bill.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          )})}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-6 p-6" data-testid="utility-ledger">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-500">This Month Total</div>
            <div className="text-2xl font-bold text-blue-600">
              ₹{summary.thisMonthTotal.toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-500">This Month Pending</div>
            <div className="text-2xl font-bold text-red-600">
              ₹{summary.thisMonthPending.toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-500">Old Months Total</div>
            <div className="text-2xl font-bold text-blue-600">
              ₹{summary.oldMonthTotal.toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-500">Old Months Pending</div>
            <div className="text-2xl font-bold text-orange-600">
              ₹{summary.oldMonthPending.toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="ledger" className="gap-2">
            <Building2 className="w-4 h-4" />
            Utility-wise Ledger
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-2">
            <Zap className="w-4 h-4" />
            Pending Bills
            {(thisMonthBills.length + oldMonthBills.length) > 0 && (
              <Badge variant="secondary" className="ml-1">
                {thisMonthBills.length + oldMonthBills.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Utility-wise Ledger Tab */}
        <TabsContent value="ledger" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {selectedUtilityType 
                  ? `${selectedUtilityType.label} - Ledger` 
                  : 'Utility-wise Ledger'}
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                {selectedUtilityType && (
                  <Button variant="outline" size="sm" onClick={handleBackToAllTypes}>
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back to All Utilities
                  </Button>
                )}
                <Select value={ledgerFilter} onValueChange={setLedgerFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Select Period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="total_business">Total Business</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {ledgerFilter === 'custom' && (
                  <>
                    <Input
                      type="date"
                      value={ledgerCustomStart}
                      onChange={(e) => setLedgerCustomStart(e.target.value)}
                      className="w-[140px]"
                    />
                    <span>to</span>
                    <Input
                      type="date"
                      value={ledgerCustomEnd}
                      onChange={(e) => setLedgerCustomEnd(e.target.value)}
                      className="w-[140px]"
                    />
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading || loadingTypeLedger ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500">Loading utility ledger...</p>
                </div>
              ) : selectedUtilityType ? (
                /* Utility Type Detail View */
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-blue-600">Total Bills</p>
                      <p className="text-2xl font-bold">{typeLedgerDetails.summary?.total_bills || 0}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Total Billed</p>
                      <p className="text-2xl font-bold">₹{(typeLedgerDetails.summary?.total_billed || 0).toLocaleString('en-IN')}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-green-600">Total Paid</p>
                      <p className="text-2xl font-bold">₹{(typeLedgerDetails.summary?.total_paid || 0).toLocaleString('en-IN')}</p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <p className="text-sm text-red-600">Pending</p>
                      <p className="text-2xl font-bold">₹{(typeLedgerDetails.summary?.total_pending || 0).toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                  
                  {/* Two Column Layout: Bills and Payments */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Bills */}
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Bills Received
                      </h3>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Month</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                              <TableHead className="text-right">Balance</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {typeLedgerDetails.bills?.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={3} className="text-center text-gray-500">
                                  No bills found
                                </TableCell>
                              </TableRow>
                            ) : typeLedgerDetails.bills?.map(bill => (
                              <TableRow key={bill.id}>
                                <TableCell>{bill.bill_month}</TableCell>
                                <TableCell className="text-right">
                                  ₹{(bill.amount || 0).toLocaleString('en-IN')}
                                </TableCell>
                                <TableCell className="text-right text-red-600">
                                  ₹{(bill.balance || 0).toLocaleString('en-IN')}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                    
                    {/* Payments */}
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <IndianRupee className="w-4 h-4" /> Payments Made
                      </h3>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead>Month</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                              <TableHead>Method</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {typeLedgerDetails.payments?.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center text-gray-500">
                                  No payments found
                                </TableCell>
                              </TableRow>
                            ) : typeLedgerDetails.payments?.map(payment => (
                              <TableRow key={payment.id}>
                                <TableCell>
                                  {payment.paid_at?.split('T')[0] || '-'}
                                </TableCell>
                                <TableCell>{payment.bill_month}</TableCell>
                                <TableCell className="text-right text-green-600">
                                  ₹{(payment.amount || 0).toLocaleString('en-IN')}
                                </TableCell>
                                <TableCell>{payment.payment_method || 'cash'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Utility Type Summary View */
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Utility Type</TableHead>
                      <TableHead className="text-right">Bills Count</TableHead>
                      <TableHead className="text-right">Total Billed</TableHead>
                      <TableHead className="text-right">Total Paid</TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {utilityTypes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                          No utility bills found
                        </TableCell>
                      </TableRow>
                    ) : utilityTypes.map(type => (
                      <TableRow 
                        key={type.bill_type} 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSelectUtilityType(type)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2 text-blue-600 font-medium">
                            {getIcon(type.bill_type)}
                            {type.label}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{type.total_bills}</TableCell>
                        <TableCell className="text-right">
                          ₹{(type.total_billed || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          ₹{(type.total_paid || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell className="text-right text-red-600 font-medium">
                          ₹{(type.total_pending || 0).toLocaleString('en-IN')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Bills Tab (for payment) */}
        <TabsContent value="pending" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Pending Utility Bills
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { fetchPendingBills(); fetchTypeSummary(); }} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                {getSelectedCount() > 0 && (
                  <>
                    <Button variant="outline" size="sm" onClick={clearSelection}>
                      Clear Selection
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={handleOpenPaymentDialog}
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      Pay Selected ({getSelectedCount()}) - ₹{getTotalPayment().toLocaleString()}
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Tabs value={pendingTab} onValueChange={(v) => { setPendingTab(v); clearSelection(); }}>
                <div className="flex justify-between items-center mb-4">
                  <TabsList>
                    <TabsTrigger value="this_month" className="flex items-center gap-2">
                      This Month
                      {thisMonthBills.length > 0 && (
                        <Badge variant="secondary">{thisMonthBills.length}</Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="old_months" className="flex items-center gap-2">
                      Old Months
                      {oldMonthBills.length > 0 && (
                        <Badge variant="secondary">{oldMonthBills.length}</Badge>
                      )}
                    </TabsTrigger>
                  </TabsList>
                  <Button variant="outline" size="sm" onClick={selectAllInPendingTab}>
                    Select All
                  </Button>
                </div>

                <TabsContent value="this_month">
                  {renderPendingBillsTable(thisMonthBills)}
                </TabsContent>
                <TabsContent value="old_months">
                  {renderPendingBillsTable(oldMonthBills)}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Method Dialog */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Payment Method</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Total Payment: <span className="font-bold text-lg text-cyan-600">₹{getTotalPayment().toLocaleString()}</span>
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                className={`h-24 flex flex-col items-center justify-center gap-2 ${paymentMethod === 'cash' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                onClick={() => setPaymentMethod('cash')}
              >
                <Banknote className="w-8 h-8" />
                <span>Cash</span>
                <span className="text-xs opacity-75">(Via Day Closing)</span>
              </Button>
              <Button
                variant={paymentMethod === 'bank' ? 'default' : 'outline'}
                className={`h-24 flex flex-col items-center justify-center gap-2 ${paymentMethod === 'bank' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                onClick={() => setPaymentMethod('bank')}
              >
                <CreditCard className="w-8 h-8" />
                <span>Bank</span>
                <span className="text-xs opacity-75">(Direct Transfer)</span>
              </Button>
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setPaymentDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handlePayBills}
                disabled={paying}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {paying ? 'Processing...' : 'Confirm Payment'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UtilityLedger;
